import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import {
  CommonMediaQueries,
  DIALOG_DATA,
  IconModule,
  MediaScreenService,
} from '@hglobal/fin-ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';

import {
  ButtonComponent,
  DetailsValueComponent,
  ShimmerComponent,
} from '@iw/common';
import { DialogData, OperationStatus } from '@iw/core/entities';
import { IwDecimalPipe } from '@iw/core/pipes';
import { IwDialogService } from '@iw/core/services';
import { environment } from '@iw/environment';

import { OrderDetailsDialogData, TAB, TabType } from './details.entities';
import { OrdersHistoryStore } from '../../orders-history.store';
import { MessagesComponent } from './messages/messages.component';
import { OrderStatusComponent } from './order-status';
import { CancelConfirmDialogComponent } from '../cancel-confirm-dialog/cancel-confirm-dialog.component';

@Component({
  selector: 'iw-order-details',
  styleUrl: './details.component.scss',
  templateUrl: './details.component.html',
  imports: [
    DatePipe,
    IconModule,
    TranslateModule,
    ButtonComponent,
    ShimmerComponent,
    DetailsValueComponent,
    MessagesComponent,
    OrderStatusComponent,
    IwDecimalPipe,
  ],
})
export class DetailsComponent implements OnInit, OnDestroy {
  readonly #injector = inject(Injector);
  readonly #store = inject(OrdersHistoryStore);
  readonly #translate = inject(TranslateService);
  readonly #dialogService = inject(IwDialogService);
  readonly #mediaScreenService = inject(MediaScreenService);
  dialog =
    inject<DialogData<DetailsComponent, OrderDetailsDialogData>>(DIALOG_DATA);

  readonly TAB = TAB;
  readonly OperationStatus = OperationStatus;

  readonly tab = signal(this.dialogData.tab || TAB.DESCRIPTION);

  readonly order = this.#store.order;
  readonly loading = this.#store.isGetOrderPending;
  readonly supportLink = environment.supportLink;

  orderDetails = computed(() => this.order()?.order || null);
  orderOperation = computed(() => this.order()?.item || null);

  currency = computed(() => {
    return this.orderOperation()?.currency;
  });

  hasExchange = computed(() => {
    const order = this.orderDetails();
    const operation = this.orderOperation();
    return order.currency.symbol !== operation.currency.symbol;
  });

  isDesktop = toSignal(
    this.#mediaScreenService.mediaMatcher(CommonMediaQueries.MD),
  );

  cancelEffect = effect(() => {
    if (this.#store.isCancelOrderSuccess()) {
      this.#getOrder();
      this.#store.resetRequestStatus('cancelOrder');
    }
  });

  get dialogData() {
    return this.dialog.data;
  }

  ngOnInit() {
    this.#getOrder();
  }

  ngOnDestroy() {
    this.#store.update({ orderRes: null });
  }

  changeTab(tab: TabType) {
    this.tab.set(tab);
  }

  isActiveTab(tab: TabType) {
    return this.tab() === tab;
  }

  openCancelConfirm(): void {
    this.#dialogService
      .open<CancelConfirmDialogComponent, void>({
        components: [CancelConfirmDialogComponent],
        isOnTop: !this.isDesktop(),
      })
      .afterClosed<boolean>()
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.#store.cancelOrder({ id: this.dialogData.id });
      });
  }

  openMessages(): void {
    this.#dialogService.open<MessagesComponent, void>(
      {
        components: [MessagesComponent],
        title: this.#translate.instant('ordersHistory.details.tab.messages'),
        fullScreen: true,
        isOnTop: true,
      },
      this.#injector,
    );
  }

  #getOrder() {
    this.#store.getOrder({ id: this.dialogData.id });
  }
}
